// Parcela de acordo somada no boleto mensal da Vigor, em vez de boleto separado.
//
// Pedido de 17/10/2026: para o cliente ativo no rateio, o analista escolhe se as
// parcelas saem em fatura avulsa (como é hoje) ou se entram no boleto que o
// cliente já recebe todo mês. Regra de 17/09/2026 para o mês sem boleto: a
// parcela sai sozinha. Aqui, "sem boleto" é não ter entrado em nenhum até
// DIAS_PARA_SAIR_SEPARADA antes de vencer — a varredura gera a avulsa.
//
// A parcela acha a fatura pelo MÊS DE REFERÊNCIA, que é o da fatura da
// Equatorial onde ela vai entrar, e não o do vencimento: o boleto da fatura de
// SET vence em OUT (mediana de 40 dias depois do dia 1º do mês, medida em
// 17/10/2026). É a mesma busca que o cálculo da fatura (FaturaCalculationModal)
// já fazia para os acordos antigos, que somavam a parcela na conta do mês.
//
// Sem I/O: roda sob `node --test` e é usado pela tela e pela edge function.

export type CobrancaParcelas = 'avulsa' | 'fatura_vigor';

/** Parcela que não entrou em nenhum boleto da Vigor sai separada quando faltar
 *  isto para vencer: tempo de emitir o boleto e de ele chegar ao cliente. */
export const DIAS_PARA_SAIR_SEPARADA = 7;

/** Situações da UC em que ela recebe crédito todo mês — e, por isso, boleto.
 *  "Ativa-S/-Rateio" é associado ativo FORA do rateio: não entra. */
export const SITUACOES_NO_RATEIO = ['Ativa', '1° Ciclo', '2° Ciclo'];

export interface CondicoesSomar {
  ucId: number | null;
  situacaoUc: string | null | undefined;
  /** O boleto do acordo sai no nome do responsável pela dívida, não do titular. */
  boletoNoResponsavel: boolean;
  /** A dívida é do dono anterior da UC (troca de dono). */
  donoAnterior: boolean;
  /** Alguma fatura do acordo é de empresa parceira (Gran). */
  empresaParceira: boolean;
}

/** Por que as parcelas deste acordo NÃO podem ir no boleto da Vigor. Null = podem. */
export function motivoParaNaoSomar(c: CondicoesSomar): string | null {
  if (!c.ucId) return 'A UC do acordo não foi achada no cadastro.';
  const situacao = (c.situacaoUc || '').trim();
  if (!SITUACOES_NO_RATEIO.includes(situacao)) {
    return `A UC não está ativa no rateio (situação: ${situacao || 'em branco'}), então não recebe boleto todo mês.`;
  }
  if (c.empresaParceira) return 'A dívida é de fatura de empresa parceira: o boleto mensal não é da Vigor.';
  if (c.boletoNoResponsavel) return 'O boleto do acordo sai no nome do responsável pela dívida, e o boleto mensal é do titular.';
  if (c.donoAnterior) return 'A dívida é do dono anterior da UC, e o boleto mensal é de quem comprou.';
  return null;
}

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** "OUT/2026" → número de meses (ordenável); inválido → null. */
export function indiceMes(mes: string | null | undefined): number | null {
  const [abrev, ano] = String(mes || '').trim().toUpperCase().split('/');
  const i = MESES.indexOf(abrev);
  const a = Number(ano);
  return i < 0 || !Number.isInteger(a) || a < 2000 ? null : a * 12 + i;
}

const textoMes = (indice: number) => `${MESES[indice % 12]}/${Math.floor(indice / 12)}`;

const indiceDaData = (dataIso: string) => {
  const [ano, mes] = dataIso.slice(0, 10).split('-').map(Number);
  return ano * 12 + (mes - 1);
};

/**
 * Quantos meses depois do mês de referência o boleto mensal desta UC vence.
 * Vale o que mais se repete nas faturas recebidas (empate: a mais recente, que
 * vem primeiro). Sem histórico, 1 — o boleto de SET vence em OUT.
 */
export function defasagemDaUc(
  faturasRecentes: Array<{ mes_referencia?: string | null; vencimento_boleto?: string | null }>,
): number {
  const contagem = new Map<number, number>();
  const ordem: number[] = [];
  for (const f of faturasRecentes) {
    const mes = indiceMes(f.mes_referencia);
    if (mes === null || !f.vencimento_boleto) continue;
    const d = indiceDaData(f.vencimento_boleto) - mes;
    if (d < 0 || d > 2) continue;
    if (!contagem.has(d)) ordem.push(d);
    contagem.set(d, (contagem.get(d) || 0) + 1);
  }
  if (ordem.length === 0) return 1;
  return ordem.reduce((melhor, d) => (contagem.get(d)! > contagem.get(melhor)! ? d : melhor), ordem[0]);
}

/** Mês da fatura em que a parcela entra: aquela cujo boleto vence no mês do vencimento da parcela. */
export function mesDaFaturaDaParcela(dataVencimento: string, defasagem: number): string {
  return textoMes(indiceDaData(dataVencimento) - defasagem);
}

const diasEntre = (de: string, ate: string) =>
  Math.round((Date.parse(`${ate.slice(0, 10)}T00:00:00Z`) - Date.parse(`${de.slice(0, 10)}T00:00:00Z`)) / 86_400_000);

/** A parcela ainda não entrou em boleto nenhum e o vencimento chegou perto: sai separada agora. */
export function deveSairSeparada(
  p: { tipo?: string | null; status?: string | null; fatura_id?: unknown; data_vencimento?: string | null },
  hoje: string,
  dias = DIAS_PARA_SAIR_SEPARADA,
): boolean {
  if ((p.tipo ?? 'parcela') !== 'parcela') return false;
  if (p.status !== 'pendente' || (p.fatura_id !== null && p.fatura_id !== undefined)) return false;
  if (!p.data_vencimento) return false;
  return diasEntre(hoje, p.data_vencimento) <= dias;
}

/**
 * O cálculo da fatura mensal pode somar a parcela deste acordo?
 *
 * Só acordo que já vale (ativo ou em atraso): antes disso a parcela ainda é
 * proposta — somar e o acordo expirar deixaria o cliente pagando parcela de um
 * acordo que nunca existiu. E só quem escolheu somar, ou o acordo antigo
 * (anterior a 15/09/2026), que sempre foi cobrado assim.
 */
export function acordoSomaNaFaturaMensal(acordo: {
  status?: string | null;
  cobranca_parcelas?: string | null;
  faturas_origem_snapshot?: unknown;
  termo_id_autentique?: string | null;
}): boolean {
  if (acordo.status !== 'ativo' && acordo.status !== 'em_atraso') return false;
  if (acordo.cobranca_parcelas === 'fatura_vigor') return true;
  return !acordo.faturas_origem_snapshot && !acordo.termo_id_autentique;
}

/**
 * Vencimento que conta para o atraso. A parcela que entrou no boleto mensal
 * vence com ele: o cliente não está atrasado enquanto o boleto não vencer.
 */
export function vencimentoQueConta(p: {
  data_vencimento: string;
  fatura?: { vencimento_boleto?: string | null } | null;
}): string {
  return p.fatura?.vencimento_boleto?.slice(0, 10) || p.data_vencimento;
}
